import type { DatabookSheetPreview } from "@/lib/api/databook";
import type { DatabookCellPosition } from "./databookCellSelection";

export interface DatabookCellReferenceTarget {
  sheetName: string;
  cellAddresses: string[];
  versionId: string | null;
}

export function parseDatabookCellAddress(address: string): DatabookCellPosition | null {
  const match = /^([A-Z]{1,3})([1-9][0-9]{0,6})$/.exec(address);
  if (!match) return null;
  const row = Number(match[2]) - 1;
  const column = [...match[1]].reduce((value, letter) => value * 26 + letter.charCodeAt(0) - 64, 0) - 1;
  return row < 1048576 && column < 16384 ? { row, column } : null;
}

export function projectDatabookCellReferences(
  sheet: DatabookSheetPreview,
  addresses: readonly string[],
): DatabookCellPosition[] {
  const rows = new Map(sheet.rows.map(row => [row.source_index, row.index]));
  const columns = new Map(sheet.columns.map(column => [column.source_index, column.index]));
  return addresses.flatMap(address => {
    const source = parseDatabookCellAddress(address);
    if (!source) return [];
    const row = rows.get(source.row);
    const column = columns.get(source.column);
    if (row === undefined || column === undefined) return [];
    const merge = sheet.merges.find(merge => row >= merge.start_row && row <= merge.end_row
      && column >= merge.start_column && column <= merge.end_column);
    return [{ row: merge?.start_row ?? row, column: merge?.start_column ?? column }];
  });
}

export function databookCellReferenceGroups(
  sheet: DatabookSheetPreview,
  addresses: readonly string[],
): { rows: string[]; columns: string[] } {
  const positions = addresses.map(parseDatabookCellAddress).filter(position => position !== null);
  return {
    rows: sheet.outline_groups.filter(group => positions.some(position =>
      position.row >= group.start_source_index && position.row <= group.end_source_index)).map(group => group.id),
    columns: sheet.column_outline_groups.filter(group => positions.some(position =>
      position.column >= group.start_source_index && position.column <= group.end_source_index)).map(group => group.id),
  };
}
